import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, of, shareReplay } from "rxjs";
import { dedupeUsersFromOwnershipRows } from "src/app/shared/utils/order-users-dedupe";
import { APIResponse } from "src/app/core/models/api-response";
import { environment } from "src/environments/environment";
import { IOrder, IOrderFilterRequest, IOrderUser, IOrdersKPIs } from "../models/order.model";
import { IPageCount } from "src/app/shared/models/page.model";

@Injectable({
  providedIn: 'root',
})
export class OrdersService {

  private backofficeUsersCache$?: Observable<IOrderUser[]>;

  constructor(private http: HttpClient) { }

  public getOrders(
    page: IPageCount,
    filter?: IOrderFilterRequest | undefined,
    search?: string | undefined,
    sort_by: string = 'id',
    sort_mode: string = 'desc',
  ): Observable<APIResponse<IOrdersKPIs>> {
    let params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('filters', JSON.stringify(filter ?? { filters: [] }))
      .set('sort_by', sort_by)
      .set('sort_mode', sort_mode);
    const key = (search ?? '').trim();
    if (key) {
      params = params.set('key', key);
    }
    return this.http.get<APIResponse<IOrdersKPIs>>(
      environment.BASE_URL_API + 'orders', { params }
    );
  }

  /**
   * Active internal users for owner/creator pickers (GET /v1/users/search).
   * Prefer this over searchUsersViaOrdersKey so pickers are not limited to users
   * who already appear on a matching order row.
   */
  searchBackofficeUsers(query: string, limit = 20): Observable<IOrderUser[]> {
    const q = (query ?? '').trim();
    const lim = Math.min(20, Math.max(1, limit));
    let params = new HttpParams().set('limit', String(lim));
    if (q) {
      params = params.set('q', q);
    }
    return this.http
      .get<APIResponse<IOrderUser>>(`${environment.BASE_URL_API}users/search`, { params })
      .pipe(map((res) => res.data ?? []));
  }

  /**
   * Default (unfiltered) active internal users for owner/creator pickers, so a
   * picker can show an initial list before the user types. Same endpoint/set as
   * searchBackofficeUsers, just without a query.
   */
  listBackofficeUsers(limit = 20): Observable<IOrderUser[]> {
    if (!this.backofficeUsersCache$) {
      const lim = Math.min(20, Math.max(1, limit));
      const params = new HttpParams().set('limit', String(lim));
      this.backofficeUsersCache$ = this.http
        .get<APIResponse<IOrderUser>>(`${environment.BASE_URL_API}users/search`, { params })
        .pipe(map((res) => res.data ?? []), shareReplay(1));
    }
    return this.backofficeUsersCache$;
  }

  /**
   * Resolve users from GET /v1/orders `key` search (creators/owners on matching orders only).
   */
  searchUsersViaOrdersKey(query: string, limit = 50): Observable<IOrderUser[]> {
    const q = (query ?? '').trim();
    if (!q) {
      return of([]);
    }
    return this.getOrders(
      { number: 1, size: limit },
      { filters: [] },
      q
    ).pipe(map((res) => dedupeUsersFromOwnershipRows(res.object?.orders ?? [])));
  }

  public exportOrders(
    filter?: IOrderFilterRequest | undefined,
    search?: string | undefined
  ){
    let params = new HttpParams().set(
      'filters',
      JSON.stringify(filter ?? { filters: [] })
    );
    const key = (search ?? '').trim();
    if (key) {
      params = params.set('key', key);
    }
    return this.http.get(environment.BASE_URL_API + 'orders/export', {
      responseType: 'blob', params
    });
  }

  /**
   * PATCH /v1/orders/:id/owner — resolves on any 2xx without requiring a specific
   * response shape. Use when the caller already holds the selected user object
   * (e.g. inline owner picker) and just needs to know the write succeeded.
   */
  assignOrderOwner(orderId: number, ownerId: number, note?: string): Observable<unknown> {
    return this.http.patch(
      `${environment.BASE_URL_API}orders/${orderId}/owner`,
      { owner_id: ownerId, note: note ?? '' }
    );
  }

  /** PATCH /v1/orders/:id/owner — operational owner only (creator stays create_uid). */
  updateOrderOwner(orderId: number, ownerId: number, note?: string): Observable<IOrderUser> {
    return this.http
      .patch<{ object?: { owner: IOrderUser }; message?: string }>(
        `${environment.BASE_URL_API}orders/${orderId}/owner`,
        { owner_id: ownerId, note: note ?? '' }
      )
      .pipe(
        map((res) => {
          const owner = res.object?.owner;
          if (!owner) {
            throw new Error('Missing owner in response');
          }
          return owner;
        })
      );
  }
}
